/**
 * Utilities for handling external links in PWA context
 */

/**
 * Opens a URL in the appropriate context based on the current environment
 * - In standalone PWA mode: opens in a new browser window
 * - In regular browser: opens in a new tab
 *
 * @param {string} url - The URL to open
 * @param {boolean} forceExternal - Force opening in external browser even if same origin
 * @param {Object} options - Additional options
 * @param {string} options.returnUrl - URL to return to after payment
 * @returns {boolean} - True if successfully handled
 */
export function openExternalLink(url, forceExternal = false, options = {}) {
  // Check if we're in standalone PWA mode
  const isStandalone = window.matchMedia('(display-mode: standalone)').matches ||
                       window.navigator.standalone ||
                       document.referrer.includes('android-app://');

  // Check if it's a payment-related URL
  const isPaymentUrl = isPaymentLink(url);

  // For payment URLs, modify to include return URL if needed
  if (isPaymentUrl) {
    try {
      // Add return URL for payment providers that support it
      const paymentUrl = new URL(url);

      // Get the base URL of the current app
      const appBaseUrl = window.location.origin;

      // Use our static HTML payment return page instead of the Vue route
      // This bypasses the PWA service worker and Vue router completely
      const returnUrl = `${appBaseUrl}/payment-return.html`;

      // Add return URL as query parameter for payment providers
      // Different payment providers use different parameter names
      if (!paymentUrl.searchParams.has('redirect_url') &&
          !paymentUrl.searchParams.has('callback_url') &&
          !paymentUrl.searchParams.has('return_url')) {

        paymentUrl.searchParams.append('redirect_url', returnUrl);
        paymentUrl.searchParams.append('callback_url', returnUrl);
        paymentUrl.searchParams.append('return_url', returnUrl);
      }

      // Update the URL with our modified version
      url = paymentUrl.toString();
    } catch (error) {
      console.error('Error modifying payment URL:', error);
    }
  }

  // Always open payment links externally
  if (isPaymentUrl || forceExternal) {
    if (isStandalone) {
      // In PWA mode, use more reliable handling for all platforms
      try {
        // Create a visible link element that the user can see and tap
        // This is more reliable than programmatic window.open() which may be blocked
        const paymentRedirectEl = document.getElementById('payment-redirect-link') || document.createElement('a');
        paymentRedirectEl.id = 'payment-redirect-link';
        paymentRedirectEl.href = url;
        paymentRedirectEl.target = '_blank';
        paymentRedirectEl.rel = 'noopener noreferrer';
        paymentRedirectEl.style.position = 'fixed';
        paymentRedirectEl.style.top = '0';
        paymentRedirectEl.style.left = '0';
        paymentRedirectEl.style.width = '100%';
        paymentRedirectEl.style.height = '100%';
        paymentRedirectEl.style.backgroundColor = 'rgba(0,0,0,0.8)';
        paymentRedirectEl.style.zIndex = '999999';
        paymentRedirectEl.style.display = 'flex';
        paymentRedirectEl.style.flexDirection = 'column';
        paymentRedirectEl.style.alignItems = 'center';
        paymentRedirectEl.style.justifyContent = 'center';
        paymentRedirectEl.style.color = 'white';
        paymentRedirectEl.style.textAlign = 'center';
        paymentRedirectEl.style.padding = '20px';
        paymentRedirectEl.style.boxSizing = 'border-box';

        // Add instructions for the user
        paymentRedirectEl.innerHTML = `
          <div style="font-size: 18px; font-weight: bold; margin-bottom: 20px;">Payment Page</div>
          <div style="margin-bottom: 30px;">To complete your payment, you'll need to open the payment page in your browser</div>
          <div style="display: flex; gap: 20px; margin-bottom: 20px;">
            <button id="open-payment-link" style="
              padding: 12px 24px;
              background-color: #4CAF50;
              color: white;
              border: none;
              border-radius: 4px;
              font-size: 16px;
              cursor: pointer;
              display: flex;
              align-items: center;
              gap: 8px;
            ">
              <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2">
                <path d="M18 13v6a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2V8a2 2 0 0 1 2-2h6"></path>
                <polyline points="15 3 21 3 21 9"></polyline>
                <line x1="10" y1="14" x2="21" y2="3"></line>
              </svg>
              Open Payment Page
            </button>
            <button id="cancel-payment" style="
              padding: 12px 24px;
              background-color: #f44336;
              color: white;
              border: none;
              border-radius: 4px;
              font-size: 16px;
              cursor: pointer;
              display: flex;
              align-items: center;
              gap: 8px;
            ">
              <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2">
                <line x1="18" y1="6" x2="6" y2="18"></line>
                <line x1="6" y1="6" x2="18" y2="18"></line>
              </svg>
              Cancel
            </button>
          </div>
          <div style="font-size: 14px; color: #aaa;">
            Note: You'll be redirected back to the app after completing your payment
          </div>
        `;

        // Add to document if not already there
        if (!paymentRedirectEl.parentNode) {
          document.body.appendChild(paymentRedirectEl);
        }

        // Function to remove the overlay
        const removeOverlay = () => {
          if (paymentRedirectEl && paymentRedirectEl.parentNode) {
            paymentRedirectEl.parentNode.removeChild(paymentRedirectEl);
          }
        };

        // Handle opening the payment link
        const openPaymentButton = document.getElementById('open-payment-link');
        if (openPaymentButton) {
          openPaymentButton.addEventListener('click', (e) => {
            e.preventDefault();
            e.stopPropagation();
            window.open(url, '_blank', 'noopener,noreferrer');
            removeOverlay();
          });
        }

        // Handle cancel button
        const cancelButton = document.getElementById('cancel-payment');
        if (cancelButton) {
          cancelButton.addEventListener('click', (e) => {
            e.preventDefault();
            e.stopPropagation();
            removeOverlay();
          });
        }

        // Prevent clicks on the overlay from bubbling
        paymentRedirectEl.addEventListener('click', (e) => {
          e.preventDefault();
          e.stopPropagation();
        });

        return true;
      } catch (e) {
        console.error('Error setting up payment redirect:', e);
        // Fall back to standard approach
        window.open(url, '_blank', 'noopener,noreferrer');
        return true;
      }
    } else {
      // In browser mode, just open in new tab
      window.open(url, '_blank', 'noopener,noreferrer');
      return true;
    }
  }

  // For non-payment links, let the default navigation handle it
  return false;
}

/**
 * Checks if a URL is likely a payment-related link
 *
 * @param {string} url - The URL to check
 * @returns {boolean} - True if it appears to be a payment link
 */
export function isPaymentLink(url) {
  if (!url) return false;

  const paymentDomains = [
    'paystack.com',
    'flutterwave.com',
    'stripe.com',
    'paypal.com',
    'checkout.com',
    'pay.google.com',
    'pay.apple.com',
    'checkout.paystack',
    'checkout.flutterwave',
    'payment',
    'gateway',
    'checkout',
    'transaction'
  ];

  // Convert URL to lowercase for case-insensitive matching
  const lowerUrl = url.toLowerCase();

  // Check if URL contains any payment-related domains
  return paymentDomains.some(domain => lowerUrl.includes(domain)) ||
         // Check for common payment-related paths
         /\/(pay|checkout|payment|transaction|gateway)\//.test(lowerUrl);
}

/**
 * Adds click event listeners to all payment links on a page
 * Call this function when the page loads to ensure proper handling
 */
export function setupPaymentLinkHandling() {
  document.addEventListener('click', (event) => {
    // Find closest anchor element
    const link = event.target.closest('a');

    if (link && link.href) {
      // Check if it's a payment link
      if (isPaymentLink(link.href)) {
        event.preventDefault();
        openExternalLink(link.href, true);
      }
    }
  });
}
